import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { wahooIntegrationService } from '../services/wahooIntegrationService';
import { hammerheadIntegrationService } from '../services/hammerheadIntegrationService';
import { db, storage } from '../firebase';
import { storageService } from '../services/storageService';
import FileUpload from '../components/FileUpload';

const MobileSyncPage: React.FC = () => {
    const { userProfile, currentUser } = useAuth();
    const [uploadStatus, setUploadStatus] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

    const wahooConnected = (userProfile as any)?.wahoo?.connected;
    const hammerheadConnected = (userProfile as any)?.hammerhead?.connected;

    const handleManualUpload = async (file: File) => {
        if (!file || !currentUser) return;

        try {
            await storageService.uploadPendingRaceFile(file, currentUser.uid, setUploadStatus);
        } catch (error) {
            console.error('Manual upload failed:', error);
            setUploadStatus({ message: error instanceof Error ? error.message : 'Upload failed. Please try again.', type: 'error' });
            throw error; // Re-throw so FileUpload component resets its loading state
        }
    };

    return (
        <div className="flex flex-col min-h-screen p-6 animate-fade-in max-w-md mx-auto">
            <div className="mt-8 mb-8 text-center">
                <h2 className="text-2xl font-bold text-white">Sync Sources</h2>
                <p className="text-gray-400 mt-2 text-sm">Connect your head unit to automatically upload race files.</p>
                
                <svg className="w-full h-8 mt-4" viewBox="0 0 100 20">
                    <path d="M0,10 Q25,20 50,10 T100,10" fill="none" stroke="#22d3ee" strokeWidth="2" strokeDasharray="100" strokeDashoffset="100" className="animate-[drawPath_2s_ease-out_forwards] opacity-0" style={{ animationDelay: '0.2s' }} />
                </svg>
                <style>{`
                    @keyframes drawPath {
                        0% { stroke-dashoffset: 100; opacity: 0; }
                        20% { opacity: 1; }
                        100% { stroke-dashoffset: 0; opacity: 1; }
                    }
                `}</style>
            </div>

            <div className="space-y-4 flex-grow">
                {/* Wahoo Card */}
                <div className={`p-5 rounded-2xl border ${wahooConnected ? 'bg-cyan-900/20 border-cyan-500/50' : 'bg-gray-800 border-gray-700'}`}>
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-bold text-white">Wahoo</h3>
                        {wahooConnected && <span className="bg-cyan-500/20 text-cyan-400 text-xs font-bold px-2 py-1 rounded-full border border-cyan-500/30">Connected ✓</span>}
                    </div>
                    {wahooConnected ? (
                        <p className="text-sm text-gray-400 mb-4">Future rides will now sync automatically.</p>
                    ) : (
                        <p className="text-sm text-gray-500 mb-4">Sync activities from your ELEMNT, BOLT, or ROAM.</p>
                    )}
                    {!wahooConnected && (
                        <button onClick={() => wahooIntegrationService.connect()} className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl active:scale-95 transition-transform">
                            Connect Wahoo
                        </button>
                    )}
                </div>

                {/* Hammerhead Card */}
                <div className={`p-5 rounded-2xl border ${hammerheadConnected ? 'bg-cyan-900/20 border-cyan-500/50' : 'bg-gray-800 border-gray-700'}`}>
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-bold text-white">Hammerhead</h3>
                        {hammerheadConnected && <span className="bg-cyan-500/20 text-cyan-400 text-xs font-bold px-2 py-1 rounded-full border border-cyan-500/30">Connected ✓</span>}
                    </div>
                    {hammerheadConnected ? (
                         <p className="text-sm text-gray-400 mb-4">Future rides will now sync automatically.</p>
                    ) : (
                        <p className="text-sm text-gray-500 mb-4">Sync activities automatically from your Karoo dashboard.</p>
                    )}
                    {!hammerheadConnected && (
                        <button onClick={() => hammerheadIntegrationService.connect()} className="w-full bg-indigo-600 text-white font-bold py-3 rounded-xl active:scale-95 transition-transform">
                            Connect Hammerhead
                        </button>
                    )}
                </div>

                {/* Garmin Card */}
                <div className="p-5 rounded-2xl border bg-gray-800/50 border-gray-700/50 opacity-75">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-bold text-gray-300">Garmin</h3>
                        <span className="bg-gray-700 text-gray-400 text-xs font-bold px-2 py-1 rounded-full">Coming Soon</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">Support for Garmin Connect is currently in development.</p>
                    <button disabled className="w-full bg-gray-700 text-gray-500 font-bold py-3 rounded-xl cursor-not-allowed">
                        Connect Garmin
                    </button>
                </div>
            </div>

            <div className="mt-8 mb-4">
                {/* Manual Upload Card */}
                <div className="p-5 rounded-2xl border bg-gray-800 border-gray-700 mt-6">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-bold text-white">Manual Upload</h3>
                    </div>
                    <p className="text-sm text-gray-400 mb-4">Upload a .FIT, .GPX, or .TCX file directly. We'll automatically match it to the correct race and category.</p>
                    
                    {uploadStatus && (
                        <div className={`p-3 rounded-lg text-sm mb-4 ${uploadStatus.type === 'success' ? 'bg-green-900/50 text-green-300 border border-green-800' : uploadStatus.type === 'error' ? 'bg-red-900/50 text-red-300 border border-red-800' : 'bg-cyan-900/50 text-cyan-300 border border-cyan-800'}`}>
                            {uploadStatus.message}
                        </div>
                    )}
                    
                <FileUpload onFileUpload={handleManualUpload} label="Upload File" />
                </div>
            </div>
        </div>
    );
};

export default MobileSyncPage;